
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface UpdateClientData {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  address?: string;
  notes?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other';
  weightKg?: number;
  heightCm?: number;
  subscriptions?: Array<{
    workoutTypeId: string;
    sessionsPerMonth: number;
    price: number; // Already using price column
  }>;
}

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  const {currentOrganization} = useAuth();
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: async ({
      id,
      clientData
    }: {
      id: string;
      clientData: UpdateClientData;
    }) => {
      console.log('Updating client:', id, clientData);

      
      if (!currentOrganization.id) {
        throw new Error('User organization not found');
      }

      // Update client basic information
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .update({
          first_name: clientData.firstName,
          last_name: clientData.lastName,
          email: clientData.email || null,
          phone: clientData.phone,
          address: clientData.address,
          notes: clientData.notes !== undefined ? clientData.notes : null,
          birth_date: clientData.birthDate || null,
          gender: clientData.gender,
          weight_kg: clientData.weightKg || null,
          height_cm: clientData.heightCm || null,
        })
        .eq('id', id)
        .select()
        .single();

      if (clientError) {
        console.error('Error updating client:', clientError);
        throw clientError;
      }

      // Only update subscriptions if they are provided in the update data
      console.log('=== SUBSCRIPTION UPDATE CHECK ===');
      console.log('clientData.subscriptions:', clientData.subscriptions);
      console.log('clientData.subscriptions !== undefined:', clientData.subscriptions !== undefined);
      
      if (clientData.subscriptions !== undefined) {
        console.log('=== UPDATING SUBSCRIPTIONS ===');
        console.log('Received subscriptions to update:', clientData.subscriptions);
        // Get existing subscriptions
        const { data: existingSubscriptions, error: fetchError } = await supabase
          .from('client_subscriptions')
          .select('id, workout_type_id, sessions_per_month, price')
          .eq('client_id', id);

        if (fetchError) {
          console.error('Error fetching existing subscriptions:', fetchError);
          throw fetchError;
        }

        const existingWorkoutTypeIds = new Set(existingSubscriptions?.map(sub => sub.workout_type_id) || []);
        const newWorkoutTypeIds = new Set(clientData.subscriptions.map(sub => sub.workoutTypeId));

        // Find subscriptions to remove (exist in DB but not in new data)
        const subscriptionsToRemove = existingSubscriptions?.filter(sub => 
          !newWorkoutTypeIds.has(sub.workout_type_id)
        ) || [];

        // Find subscriptions to add (exist in new data but not in DB)
        const subscriptionsToAdd = clientData.subscriptions.filter(sub => 
          !existingWorkoutTypeIds.has(sub.workoutTypeId)
        );

        // Find subscriptions to update (exist in both DB and new data)
        const subscriptionsToUpdate = clientData.subscriptions.filter(sub => 
          existingWorkoutTypeIds.has(sub.workoutTypeId)
        );

        console.log('Subscriptions to remove:', subscriptionsToRemove.length);
        console.log('Subscriptions to add:', subscriptionsToAdd.length);
        console.log('Subscriptions to update:', subscriptionsToUpdate.length);

        // Remove subscriptions that are no longer needed
        if (subscriptionsToRemove.length > 0) {
          // First delete payments for these specific subscriptions only
          const subscriptionIdsToRemove = subscriptionsToRemove.map(sub => sub.id);
          
          const { error: deletePaymentsError } = await supabase
            .from('client_payments')
            .delete()
            .in('subscription_id', subscriptionIdsToRemove);

          if (deletePaymentsError) {
            console.error('Error deleting payments for removed subscriptions:', deletePaymentsError);
            throw deletePaymentsError;
          }

          // Then delete the subscriptions
          const { error: deleteError } = await supabase
            .from('client_subscriptions')
            .delete()
            .in('id', subscriptionIdsToRemove);

          if (deleteError) {
            console.error('Error deleting removed subscriptions:', deleteError);
            throw deleteError;
          }
        }

        // Update existing subscriptions
        if (subscriptionsToUpdate.length > 0) {
          console.log('Updating existing subscriptions...');
          for (const subscriptionUpdate of subscriptionsToUpdate) {
            const existingSubscription = existingSubscriptions?.find(
              sub => sub.workout_type_id === subscriptionUpdate.workoutTypeId
            );

            if (existingSubscription) {
              console.log(`Updating subscription ${existingSubscription.id}:`, {
                from: { sessions_per_month: existingSubscription.sessions_per_month, price: existingSubscription.price },
                to: { sessions_per_month: subscriptionUpdate.sessionsPerMonth, price: subscriptionUpdate.price }
              });

              const { error: updateError } = await supabase
                .from('client_subscriptions')
                .update({
                  sessions_per_month: subscriptionUpdate.sessionsPerMonth,
                  price: subscriptionUpdate.price,
                })
                .eq('id', existingSubscription.id);

              if (updateError) {
                console.error('Error updating subscription:', updateError);
                throw updateError;
              }
            }
          }
        }

        // Add new subscriptions
        if (subscriptionsToAdd.length > 0) {
          console.log('Adding new subscriptions...');
          const subscriptionsToInsert = subscriptionsToAdd.map(sub => ({
            client_id: id,
            workout_type_id: sub.workoutTypeId,
            sessions_per_month: sub.sessionsPerMonth,
            price: sub.price,
            organization_id: currentOrganization.id,
          }));

          const { error: subscriptionsError } = await supabase
            .from('client_subscriptions')
            .insert(subscriptionsToInsert);

          if (subscriptionsError) {
            console.error('Error creating new subscriptions:', subscriptionsError);
            throw subscriptionsError;
          }
        }
      }

      return client;
    },
    onSuccess: (client) => {
      // Invalidate all client-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['clients-paginated', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['clients-infinite', currentOrganization?.id] });
      // Invalidate the delete check query for this specific client
      queryClient.invalidateQueries({ queryKey: ['canDeleteClient', client.id] });
      // Invalidate specific client query
      queryClient.invalidateQueries({ queryKey: ['client', client.id] });
      // Also invalidate dashboard stats that depend on clients data
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-clients'] });
      toast.success(t('clients.updateClientSuccess'));
    },
    onError: (error) => {
      console.error('Error in update client mutation:', error);
      toast.error(t('clients.updateClientError'));
    }
  });
};
